// components
import {
  DesktopDatePicker,
  FieldSelectedSections
} from '@mui/x-date-pickers-pro';
import { IconButton } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// hooks
import { forwardRef, Ref, useEffect, useRef, useState } from 'react';

// styles
import {
  StyledCustomDatePickerTextField,
  StyledMainDatePickerBox
} from './DatePicker.styles';

// types
import { DatePickerProps } from './DatePicker.types';
import { toDate } from './DatePicker.utils';
import { PickerValue } from '@mui/x-date-pickers/internals';

// utils
import { startOfToday } from 'date-fns';
import { getMaxDate, getMinDate } from 'utils/date';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const bottomDate = getMinDate();
const topDate = getMaxDate();

const DatePicker = forwardRef(
  (props: DatePickerProps, ref: Ref<HTMLDivElement> | undefined) => {
    const {
      date: _date,
      showCalendarIconOnly,
      label,
      minDate: _minDate,
      maxDate: _maxDate,
      open: _open,
      readOnly,
      monthYearView,
      required,
      onChange,
      disabled,
      error,
      clearable = true,
      variant = 'outlined',
      size = 'small',
      autoFocus,
      sx,
      onBlur
    } = props;

    const date = _date || startOfToday();

    const minDate = _minDate || bottomDate;
    const maxDate = _maxDate || topDate;
    const [newDate, setNewDate] = useState<PickerValue>(date);
    const [selectedSection, setSelectedSection] =
      useState<FieldSelectedSections>(null);
    const isValidNewDate = dayjs(newDate).isValid();
    const [textFieldFocus, setTextFieldFocus] = useState(autoFocus);
    const [open, setOpen] = useState(_open || false);

    const inputRef = useRef<HTMLInputElement>(null);
    const iconButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      if (_date) setNewDate(_date);
    }, [_date]);

    return (
      <StyledMainDatePickerBox>
        <DesktopDatePicker
          className="date-picker"
          minDate={minDate}
          maxDate={maxDate}
          onChange={(value) => {
            if (!dayjs(value).isValid()) return;
            setTextFieldFocus(true);

            const valueOnChange = toDate(value);
            if (valueOnChange) setNewDate?.(value);

            showCalendarIconOnly && onChange?.(dayjs(value).toDate());
          }}
          selectedSections={selectedSection}
          onSelectedSectionsChange={setSelectedSection}
          slots={{
            textField: (props) =>
              showCalendarIconOnly ? null : (
                <StyledCustomDatePickerTextField
                  {...props}
                  fullWidth
                  required={required}
                  error={!!error}
                  helperText={error?.message ?? null}
                  onBlur={() => {
                    if (isValidNewDate) {
                      onChange?.(dayjs(newDate).toDate());
                      setTextFieldFocus(false);
                      setSelectedSection(0);
                    }
                    onBlur?.();
                  }}
                  variant={variant}
                  onKeyDown={(e) => {
                    if (
                      e.defaultPrevented ||
                      e.code !== 'Enter' ||
                      !isValidNewDate
                    )
                      return;

                    onChange?.(dayjs(newDate).toDate());
                    setTextFieldFocus(false);
                  }}
                  onKeyDownCapture={(e) => {
                    if (
                      (e.code === 'Delete' || e.code === 'Backspace') &&
                      selectedSection === 'all'
                    ) {
                      setNewDate(null);
                      setTextFieldFocus(true);
                    }
                  }}
                  onClickCapture={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('button')) return;
                    setTextFieldFocus(true);
                  }}
                  size={size}
                  sx={sx}
                  autoFocus={textFieldFocus}
                />
              )
          }}
          slotProps={{
            field: { clearable },
            textField: {
              inputRef,
              inputProps: { tabIndex: 0 },
              InputProps: { tabIndex: -1 }
            },
            clearButton: {
              onClickCapture: () => {
                setNewDate(null);
                setTextFieldFocus(true);
              },
              tabIndex: -1
            },
            openPickerButton: {
              tabIndex: -1
            },
            ...(showCalendarIconOnly && {
              popper: {
                anchorEl: iconButtonRef.current
              }
            })
          }}
          enableAccessibleFieldDOMStructure={false}
          displayWeekNumber
          value={newDate}
          label={label}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          readOnly={readOnly}
          disabled={disabled}
          ref={ref}
          views={monthYearView ? ['year', 'month'] : ['year', 'month', 'day']}
        />
        {showCalendarIconOnly && (
          <IconButton ref={iconButtonRef} onClick={() => setOpen(true)}>
            <CalendarMonthIcon />
          </IconButton>
        )}
      </StyledMainDatePickerBox>
    );
  }
);

export default DatePicker;
